// ===========================================================================
// ReHome Shared Types — camelCase convention (matches design_plan prototype)
// Supabase client auto-converts snake_case ↔ camelCase
// ===========================================================================

// ─── Enums ──────────────────────────────────────────────────
export type Species = 'Kutya' | 'Macska' | 'Egyéb';
export type Gender = 'Kan' | 'Nőstény';
export type AnimalStatus = 'Available' | 'AdoptionPending' | 'Adopted' | 'Foster' | 'Medical' | 'StrayHold' | 'Quarantine';

/**
 * A státuszok magyar megnevezése — EGY helyen.
 *
 * Eddig ez a szótár oldalanként újra le volt írva, ami előbb-utóbb
 * szétcsúszik: egy új státusz vagy egy átfogalmazott felirat csak az egyik
 * helyen érvényesült volna.
 */
export const ANIMAL_STATUS_LABELS: Record<AnimalStatus, string> = {
  Available: 'Gazdikereső',
  AdoptionPending: 'Örökbefogadás alatt',
  Adopted: 'Örökbefogadva',
  Foster: 'Ideiglenesben',
  Medical: 'Orvosi',
  StrayHold: 'Várakozási idő',
  Quarantine: 'Karantén',
};
export type IntakeType = 'Stray' | 'Surrender' | 'Confiscated' | 'Return';
export type ChipProvider = '24PetWatch' | 'Petlog' | 'MAVK' | 'Unknown';
export type ChipStatus = 'Registered' | 'Unregistered' | 'Lost_Reported';
export type LeadStatus = 'Pending' | 'New' | 'Contacted' | 'Vetting' | 'Approved' | 'Rejected';
export type UpdateType = '3_Day' | '3_Week' | '3_Month' | 'Spontaneous';
export type Mood = 'Happy' | 'Neutral' | 'Struggling';
export type SupporterTierName = 'Bronz' | 'Ezüst' | 'Arany Gazda';
export type TransactionType = 'Income' | 'Expense';
export type TransactionCategory = 'Donation' | 'Adoption' | 'Subscription' | 'CampaignPayout';
export type NotificationCategory = 'PostAdoption' | 'Adoption' | 'Campaign' | 'User' | 'System' | 'Security' | 'Medical' | 'Inventory';
export type NotificationSeverity = 'Critical' | 'High' | 'Medium' | 'Low';
export type StaffRole = 'Staff' | 'Admin';
export type TaskType = 'Vaccination' | 'Checkup' | 'Surgery' | 'Medication' | 'LabWork' | 'Imaging';
// 'Neutering' — l. 039_medical_neutering_and_vocab.sql. Az állat profilján
// mindig is ott volt az "Ivartalanítva" jelölő, de esemény-típusként nem
// létezett, így a jelölő mögött nem állt előzmény.
export type MedicalEventType = 'Vaccination' | 'Deworming' | 'FleaTickTreatment' | 'Checkup' | 'Surgery' | 'Medication' | 'Allergy' | 'Neutering' | 'Ultrasound' | 'Other';

/**
 * Tri-state válasz: a "nem tudjuk" ÉRDEMBEN más, mint a "nem".
 *
 * Bekerüléskor a legtöbb egészségügyi adat egyszerűen ismeretlen — ha ezt
 * "nem"-nek könyvelnénk el, a menhely téves információ alapján döntene
 * (pl. fölöslegesen újraoltana, vagy épp nem oltana).
 */
export type TriState = 'yes' | 'no' | 'unknown';

export interface VaccinationKind {
  /** Stabil, ASCII azonosító — ez kerül az `animals.vaccinations` kulcsába. */
  key: string;
  label: string;
  /** Mely fajoknál releváns. */
  species: Species[];
  /** Rövid magyarázat a felülethez. */
  hint?: string;
}

/**
 * Az oltásfajták, amikre bekerüléskor rákérdezünk.
 *
 * A kulcsok STABILAK: a `vaccinations` JSONB-ben ezek szerepelnek, tehát egy
 * kulcs átnevezése a meglévő adatot értelmezhetetlenné tenné. A címke
 * szabadon változtatható.
 */
export const VACCINATIONS: VaccinationKind[] = [
  { key: 'rabies', label: 'Veszettség', species: ['Kutya', 'Macska'], hint: 'Magyarországon kutyáknál kötelező' },
  { key: 'combined', label: 'Kombinált', species: ['Kutya', 'Macska'], hint: 'Kutya: DHPPi · Macska: panleukopenia/rhinotracheitis/calici' },
  { key: 'parvo', label: 'Parvovírus', species: ['Kutya'] },
  { key: 'kennel_cough', label: 'Kennelköhögés', species: ['Kutya'], hint: 'Bordetella — csoportos tartásnál fontos' },
  { key: 'lyme', label: 'Lyme-kór', species: ['Kutya'] },
  { key: 'felv', label: 'Leukózis (FeLV)', species: ['Macska'] },
];

/** Az adott fajnál értelmes oltásfajták. */
export const vaccinationsForSpecies = (species: Species): VaccinationKind[] =>
  VACCINATIONS.filter(v => v.species.includes(species));

/** Oltás-azonosító -> állapot. A hiányzó kulcs = "még rá sem kérdeztünk". */
export type VaccinationMap = Record<string, TriState>;

/**
 * Szívféreg-teszt eredménye.
 *
 * FIGYELEM: itt a `positive` a ROSSZ hír (fertőzött) — ellentétben az
 * oltásoknál használt `yes`-szel. Ezért külön szótár, hogy a felületen se
 * lehessen összekeverni a kettőt.
 */
export type HeartwormResult = 'positive' | 'negative' | 'unknown';

export const HEARTWORM_LABELS: Record<HeartwormResult, string> = {
  positive: 'Pozitív (fertőzött)',
  negative: 'Negatív',
  unknown: 'Nem tudjuk',
};

/**
 * Betöltött évek száma a születési dátumból.
 *
 * Az `animals.age` oszlop megmarad (szűrés és index épül rá), de a
 * születési dátum a pontosabb adat: az `age` abból származik, nem fordítva.
 */
export const ageFromBirthDate = (birthDate: string | null | undefined, now: Date = new Date()): number | null => {
  if (!birthDate) return null;
  const born = new Date(birthDate);
  if (Number.isNaN(born.getTime())) return null;
  let years = now.getFullYear() - born.getFullYear();
  const beforeBirthday =
    now.getMonth() < born.getMonth() ||
    (now.getMonth() === born.getMonth() && now.getDate() < born.getDate());
  if (beforeBirthday) years -= 1;
  return years < 0 ? null : years;
};

/** Az összes egészségügyi esemény-típus, űrlap-sorrendben. */
export const MEDICAL_EVENT_TYPES: MedicalEventType[] = [
  'Vaccination', 'Deworming', 'FleaTickTreatment', 'Checkup',
  'Surgery', 'Medication', 'Allergy', 'Neutering', 'Ultrasound', 'Other',
];

/**
 * Magyar címkék az esemény-típusokhoz.
 *
 * Egy helyen: korábban az admin három oldala (AnimalMedicalPage,
 * MedicalRemindersPage, ShelterSettingsPage) külön-külön tartotta ugyanezt a
 * leképezést, így egy új típus felvétele mindhármat elrontotta.
 */
export const MEDICAL_EVENT_LABELS: Record<MedicalEventType, string> = {
  Vaccination: 'Oltás',
  Deworming: 'Féreghajtás',
  FleaTickTreatment: 'Bolha-/kullancskezelés',
  Checkup: 'Kontrollvizsgálat',
  Surgery: 'Műtét',
  Medication: 'Gyógyszeres kezelés',
  Allergy: 'Allergia',
  Neutering: 'Ivartalanítás',
  Ultrasound: 'Ultrahangos vizsgálat',
  Other: 'Egyéb',
};

/**
 * Mely egészségügyi esemény-típus melyik állatprofil-jelölőt érinti.
 *
 * A menhelyi dolgozó a bejegyzés rögzítésekor eldöntheti, hogy az adott
 * mező átálljon-e (l. AnimalMedicalPage) — nem automatikus, mert pl. egy
 * hosszabb parazitakezelés ELSŐ kezelése után az állat még nem
 * "parazitamentes".
 */
export const MEDICAL_EVENT_ANIMAL_FLAG: Partial<Record<MedicalEventType, {
  field: 'isVaccinated' | 'isDewormed' | 'isNeutered';
  /** DB oszlopnév — a mutation ezt írja. */
  column: 'is_vaccinated' | 'is_dewormed' | 'is_neutered';
  label: string;
}>> = {
  Vaccination: { field: 'isVaccinated', column: 'is_vaccinated', label: 'Oltott' },
  Deworming: { field: 'isDewormed', column: 'is_dewormed', label: 'Parazitamentes' },
  Neutering: { field: 'isNeutered', column: 'is_neutered', label: 'Ivartalanítva' },
};
export type MarketingChannel = 'Facebook' | 'Instagram' | 'TikTok';

// ─── Value Objects ──────────────────────────────────────────

export interface FoundLocation {
  lat: number;
  lng: number;
  address?: string;
  foundDate: string;
}

export interface ChipData {
  chipId: string;
  provider?: ChipProvider;
  registeredOwner?: string;
  status: ChipStatus;
}

export interface AnimalMarketing {
  activeChannels: MarketingChannel[];
  lastPostDate?: string;
  totalReach: number;
  notes?: string;
}

// ─── Medical Types ──────────────────────────────────────────

export interface Medication {
  id: string;
  name: string;
  stock: number;
  unit: string;
  minStock: number;
  expiryDate: string;
  batchNumber: string;
}

export interface Attachment {
  id: string;
  type: 'image' | 'dicom' | 'document';
  name: string;
  url: string;
  uploadDate: string;
}

export interface LabResult {
  parameter: string;
  value: string;
  unit: string;
  status: 'Normal' | 'High' | 'Low';
  referenceRange?: string;
}

export interface MedicalTask {
  id: string;
  date: string;
  type: TaskType;
  title: string;
  isCompleted: boolean;
  notes?: string;
  veterinarian?: string;
  medicationUsed?: { medicationId: string; amount: number };
  nextDueDate?: string;
  attachments?: Attachment[];
  labResults?: LabResult[];
}

// ─── Core Entities ──────────────────────────────────────────

export interface Shelter {
  id: string;
  name: string;
  slug: string;
  location: string;
  lat: number | null;
  lng: number | null;
  capacity: number;
  imageUrl: string | null;
  description: string | null;
  longStory: string | null;
  taxNumber: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  isVerified: boolean;
  /**
   * Google Place ID — bizonyíték arra, hogy a címet a Google javaslataiból
   * választották, nem kézzel gépelték (l. 040_shelter_address_...sql).
   */
  addressPlaceId: string | null;
  /** Mikor lett a cím Google-javaslatból kiválasztva. `null` = még nem validált. */
  addressVerifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
  financials?: FinancialTransaction[];
}

/** Van-e a menhelynek Google-validált, koordinátával rendelkező címe? */
export const hasVerifiedAddress = (shelter: Pick<Shelter, 'addressVerifiedAt' | 'lat' | 'lng'>): boolean =>
  !!shelter.addressVerifiedAt && shelter.lat !== null && shelter.lng !== null;

export interface Animal {
  id: string;
  shelterId: string;
  name: string;
  species: Species;
  breed: string | null;
  age: number | null;
  gender: Gender | null;
  weightKg: number | null;
  traits: string[];
  bio: string | null;
  /** Borítókép — mindig PUBLIKUS URL, vagy `null`, ha nincs publikus kép. */
  imageUrl: string | null;
  /** Publikus kép-URL-ek, megjelenítési sorrendben. A `gallery[0]` a borító. */
  gallery: string[];
  /**
   * Privát képek tároló-útvonalai (`animals/{id}/…`). Csak a menhely látja,
   * aláírt linken keresztül — l. shared/src/animalPhotos.ts.
   */
  privateGallery?: string[];
  intakeDate: string;
  intakeType: IntakeType | null;
  birthDate?: string | null;
  /** Részletes oltási állapot (l. VACCINATIONS). Az `isVaccinated` az összefoglaló. */
  vaccinations?: VaccinationMap | null;
  /** Szívféreg-teszt eredménye; `null` = nem volt teszt. */
  heartwormTest?: HeartwormResult | null;
  heartwormTestedAt?: string | null;
  activityLevel?: string | null;
  foundLocation: FoundLocation | null;
  strayHoldEndDate: string | null;
  status: AnimalStatus;
  microchipId: string | null;
  chipProvider: ChipProvider | null;
  chipStatus: ChipStatus | null;
  color: string | null;
  isVaccinated: boolean | null;
  isNeutered: boolean | null;
  isDewormed: boolean | null;
  adoptionDate: string | null;
  adopterId: string | null;
  adopterName?: string;
  adopterEmail?: string;
  adoptionFee: number | null;
  leadId?: string | null;
  activeCampaignId?: string;
  requiredAdopterFields?: string[];
  createdAt: string;
  updatedAt: string;
  marketing?: AnimalMarketing;
  medicalTasks?: MedicalTask[];
  leads?: AdoptionLead[];
  postAdoptionUpdates?: PostAdoptionUpdate[];
}

// ─── Intake Records (staff-only, never public) ──────────────

/**
 * Sensitive bekerülési adatok — külön táblában (animal_intake_records),
 * staff-only RLS-sel védve. SOSEM kerülhet az `animals` táblába, mert azt
 * a platform-frontend is `select('*')`-tal olvassa.
 */
export interface AnimalIntakeRecord {
  animalId: string;
  circumstances: string | null;
  chipOwnerInfo: string | null;
  intakeReferenceNumber: string | null;
  finderName: string | null;
  finderPhone: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Medical Events (staff-only, never public) ────────────────

/**
 * Egészségügyi eseménynapló bejegyzés — külön táblában (animal_medical_events),
 * staff-only RLS-sel védve. Egy sor = egy elvégzett/tervezett esemény (oltás,
 * féreghajtás, allergia stb.). Ismétlődő eseményeknél az újabb előfordulást
 * új sorként rögzítik — ez ad valódi előzményt, nem csak "következő esedékesség"-et.
 * Kiterjed a `MedicalTask`/`Medication`/`LabResult`/`Attachment` (fentebb) által
 * jelölt, egyelőre nem épített, gazdagabb orvosi modultól (készlet, labor, DICOM) —
 * ez utóbbi later phase, lásd plan.md §B.2.
 */
export interface AnimalMedicalEvent {
  id: string;
  animalId: string;
  eventType: MedicalEventType;
  title: string;
  eventDate: string;
  notes: string | null;
  veterinarian: string | null;
  isRecurring: boolean;
  recurrenceIntervalDays: number | null;
  nextDueDate: string | null;
  customLeadDays: number | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A recurring medical event joined with its animal's identity — used by the
 * shelter-wide "Emlékeztetők" list (useUpcomingMedicalReminders), where each
 * row needs to show which animal it belongs to.
 */
export interface AnimalMedicalReminder extends AnimalMedicalEvent {
  animal: { id: string; name: string; species: Species };
}

// ─── Breed Taxonomy ───────────────────────────────────────────

export const MIXED_BREED = 'Keverék';
export const OTHER_BREED = 'Egyéb';

export const DOG_BREEDS = [
  'Labrador retriever', 'Aranyos retriever (Golden retriever)', 'Németjuhász',
  'Border collie', 'Beagle', 'Magyar vizsla', 'Puli', 'Mudi', 'Kuvasz',
  'Komondor', 'Amerikai staffordshire terrier', 'Jack Russell terrier',
  'Yorkshire terrier', 'Chihuahua', 'Francia bulldog', 'Angol bulldog',
  'Mopsz', 'Dobermann', 'Rottweiler', 'Boxer', 'Basset hound',
  'Cocker spániel', 'Shih tzu', 'Máltai selyemkutya', 'Akita',
  'Szibériai husky', 'Alaszkai malamut', 'Dalmata', 'Pomerániai törpespicc',
  'Bichon frisé', 'Cane corso', 'Rhodesiai ridgeback', 'Weimari vizsla',
  'Foxi', 'Tacskó', 'Bernáthegyi', 'Newfoundlandi',
  MIXED_BREED, OTHER_BREED,
] as const;

export const CAT_BREEDS = [
  'Brit rövidszőrű', 'Amerikai rövidszőrű', 'Sziámi', 'Ragdoll',
  'Maine coon', 'Perzsa', 'Bengáli', 'Egyiptomi mau', 'Norvég erdei',
  'Orosz kék', 'Sphynx', 'Devon rex', 'Cornish rex', 'Abesszín', 'Birman',
  'Török angóra', 'Skót lógófülű', 'Manx', 'Burmai', 'Himalayan',
  MIXED_BREED, OTHER_BREED,
] as const;

/** Returns the curated breed list for a species — empty for 'Egyéb' (always free text there). */
export const getBreedOptions = (species: Species | string | null | undefined): readonly string[] => {
  if (species === 'Kutya') return DOG_BREEDS;
  if (species === 'Macska') return CAT_BREEDS;
  return [];
};

// ─── User & Auth ────────────────────────────────────────────

export interface UserProfile {
  id: string;
  fullName: string | null;
  phone: string | null;
  address: string | null;
  avatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShelterStaff {
  id: string;
  shelterId: string;
  userId: string;
  role: StaffRole;
  createdAt: string;
}

// ─── Adoption Pipeline ──────────────────────────────────────

export interface AdoptionLead {
  id: string;
  animalId: string;
  adopterId?: string | null;
  name: string;
  email: string;
  phone: string | null;
  message: string | null;
  status: LeadStatus;
  notes: string | null;
  /** @deprecated A `reviewNote` váltja fel — elutasításnál továbbra is kitöltött. */
  rejectionReason?: string | null;
  /** A bíráló indoklása — elfogadásnál és elutasításnál egyaránt. */
  reviewNote?: string | null;
  reviewedAt?: string | null;
  contactRevealed?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdoptionContract {
  id: string;
  animalId: string;
  adopterId: string;
  adopterName?: string;
  adopterEmail?: string;
  date?: string;
  signature?: string;
  contractText: string | null;
  contractSummary: string | null;
  signatureDataUrl: string | null;
  insurancePlan: string | null;
  insurancePlanId?: string;
  adoptionFee: number;
  fee?: number;
  extraDonation: number;
  isPaid: boolean;
  stripePaymentId: string | null;
  createdAt: string;
}

export interface InsurancePlan {
  id: string;
  name: string;
  monthlyFee: number;
  coverage: string[];
  recommended: boolean;
}

// ─── Post-Adoption ──────────────────────────────────────────

export interface PostAdoptionUpdate {
  id: string;
  animalId?: string;
  adopterId?: string;
  /** DB column: update_type */
  updateType: UpdateType;
  imageUrl: string;
  /** DB columns: gps_lat / gps_lng */
  gpsLat?: number | null;
  gpsLng?: number | null;
  mood: Mood | null;
  notes: string | null;
  documents?: { name: string; url: string; type: string; date: string }[];
  verifiedByShelter: boolean;
  shelterNotes?: string | null;
  createdAt: string;
  /** @deprecated prototype-only fields — not present in the DB schema */
  date?: string;
  type?: UpdateType;
  gpsLocation?: { lat: number; lng: number };
  aiMatchStatus?: 'MATCH' | 'MISMATCH' | 'UNCERTAIN';
  aiMatchConfidence?: number;
}

// ─── Donations & Campaigns ──────────────────────────────────

export interface DonationCampaign {
  id: string;
  shelterId?: string | null;
  animalId?: string | null;
  organizerUserId?: string | null;
  title: string;
  description?: string;
  shortDescription?: string | null;
  longDescription?: string | null;
  imageUrl?: string | null;
  targetAmount: number;
  raisedAmount: number;
  startDate?: string;
  endDate?: string | null;
  deadline?: string;
  isActive?: boolean;
  active?: boolean;
  costBreakdown?: { item: string; amount: number }[];
  infrastructureDescription?: string;
  externalLink?: string;
  acceptsVolunteers?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Donation {
  id: string;
  campaignId: string | null;
  donorId: string | null;
  amount: number;
  donorName: string | null;
  donorEmail: string | null;
  isAnonymous: boolean;
  stripePaymentId: string | null;
  message: string | null;
  createdAt: string;
}

export interface SupporterTier {
  id: string;
  name: string;
  monthlyAmount: number;
  benefits: string[];
  color: string;
}

export interface SupporterSubscription {
  id: string;
  userId: string;
  tier: SupporterTierName;
  monthlyAmount: number;
  stripeSubscriptionId: string | null;
  isActive: boolean;
  startedAt: string;
  cancelledAt: string | null;
}

// ─── Financial ──────────────────────────────────────────────

export interface FinancialTransaction {
  id: string;
  shelterId: string;
  date: string;
  amount: number;
  type: TransactionType;
  category: TransactionCategory;
  description: string | null;
  animalId?: string;
  campaignId?: string;
  donationId?: string;
  isRecurring?: boolean;
  createdAt: string;
}

// ─── Notifications ──────────────────────────────────────────

export interface AppNotification {
  id: string;
  shelterId?: string;
  category: NotificationCategory;
  severity: NotificationSeverity;
  title: string;
  message: string;
  link?: string;
  isRead?: boolean;
  timestamp: string;
  createdAt?: string;
  medicalEventId?: string | null;
  snoozedUntil?: string | null;
}

// ─── Volunteer ──────────────────────────────────────────────

export interface VolunteerApplication {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  helpType: string;
  message: string | null;
  shelterId: string | null;
  createdAt: string;
}

// ─── Match & AI ─────────────────────────────────────────────

export interface MatchResult {
  animalId: string;
  score: number;
  reason: string;
}

export interface LifestyleProfile {
  housing: string;
  activity: string;
  experience: string;
  otherPets: string;
  kids: string;
}

/**
 * A Smart Match öt életmód-kérdése — EGY helyen definiálva.
 *
 * Ugyanezt a listát rendereli a matcher (végigkattintós folyamat) és a
 * profil oldal (szerkeszthető űrlap), és ugyanezek az értékek kerülnek az
 * `adopter_profiles.lifestyle_*` oszlopokba. Korábban a kérdések a
 * GamifiedMatcherPage-ben voltak beégetve, a profil pedig egyáltalán nem
 * ismerte őket — így a két felület nem tudott közös adatból dolgozni.
 *
 * Az `value` értékek a DB CHECK megszorításaival egyeznek: bővítés esetén a
 * migrációt is bővíteni kell.
 */
export const LIFESTYLE_QUESTIONS = [
  {
    key: 'housing' as const,
    column: 'lifestyle_housing' as const,
    question: 'Milyen a lakhatásod?',
    profileLabel: 'Lakhatás típusa',
    options: [
      { value: 'house_garden', label: 'Kertes ház' },
      { value: 'apartment_large', label: 'Lakás (nagy)' },
      { value: 'apartment_small', label: 'Lakás (kicsi)' },
    ],
  },
  {
    key: 'activity' as const,
    column: 'lifestyle_activity' as const,
    question: 'Mennyire vagy aktív?',
    profileLabel: 'Aktivitási szint',
    options: [
      { value: 'very_active', label: 'Maratonfutó vagyok' },
      { value: 'moderate', label: 'Szeretek sétálni' },
      { value: 'low', label: 'Kanapéhuszár' },
    ],
  },
  {
    key: 'experience' as const,
    column: 'lifestyle_experience' as const,
    question: 'Volt már állatod?',
    profileLabel: 'Állattartási tapasztalat',
    options: [
      { value: 'experienced', label: 'Igen, tapasztalt vagyok' },
      { value: 'first_time', label: 'Még soha (Első gazdi)' },
    ],
  },
  {
    key: 'otherPets' as const,
    column: 'lifestyle_other_pets' as const,
    question: 'Vannak más állataid?',
    profileLabel: 'Más állat a háztartásban',
    options: [
      { value: 'none', label: 'Nincsenek' },
      { value: 'dog', label: 'Van kutyám' },
      { value: 'cat', label: 'Van macskám' },
    ],
  },
  {
    key: 'kids' as const,
    column: 'lifestyle_kids' as const,
    question: 'Vannak gyerekek a háztartásban?',
    profileLabel: 'Gyerekek a háztartásban',
    options: [
      { value: 'none', label: 'Nincsenek' },
      { value: 'young', label: 'Kisgyerek (0-6 év)' },
      { value: 'older', label: 'Nagyobb gyerek (7+)' },
    ],
  },
] as const;

export type LifestyleQuestionKey = typeof LIFESTYLE_QUESTIONS[number]['key'];

/** Üres (még kitöltetlen) életmód-profil. */
export const EMPTY_LIFESTYLE_PROFILE: LifestyleProfile = {
  housing: '', activity: '', experience: '', otherPets: '', kids: '',
};

/** Hány kérdésre van válasz az adott profilban. */
export const countAnsweredLifestyle = (p: LifestyleProfile | null | undefined): number =>
  p ? LIFESTYLE_QUESTIONS.filter(q => !!p[q.key]).length : 0;

/** Minden kérdés meg van válaszolva? */
export const isLifestyleComplete = (p: LifestyleProfile | null | undefined): boolean =>
  countAnsweredLifestyle(p) === LIFESTYLE_QUESTIONS.length;

// ─── Adopter Profile & Shelter Policies ─────────────────────

/** Címkézett mező nevek — az adopter_profiles tábla camelCase mezői */
export const ADOPTER_PROFILE_FIELDS = [
  { key: 'housing_type',              category: 'Lakhatás',    label: 'Lakhatás típusa',          sensitive: false, options: ['saját_lakás','albérlet','családi_ház','egyéb'] },
  { key: 'housing_details',           category: 'Lakhatás',    label: 'Lakhatás részletei',       sensitive: false },
  { key: 'household_members',         category: 'Háztartás',   label: 'Háztartás létszáma',       sensitive: false },
  { key: 'has_children',              category: 'Háztartás',   label: 'Van-e gyermek?',           sensitive: false },
  { key: 'children_ages',             category: 'Háztartás',   label: 'Gyermekek életkora',       sensitive: true  },
  { key: 'has_other_pets',            category: 'Háztartás',   label: 'Van-e más állat?',         sensitive: false },
  { key: 'other_pets_details',        category: 'Háztartás',   label: 'Más állatok részletei',    sensitive: false },
  { key: 'daily_routine',             category: 'Életmód',     label: 'Napi rutin / Egyedül töltött órák', sensitive: false },
  { key: 'work_schedule',             category: 'Életmód',     label: 'Munkarend',                sensitive: false, options: ['home_office','iroda','vegyes','egyéb'] },
  { key: 'previous_pet_experience',   category: 'Tapasztalat', label: 'Volt-e már állata?',       sensitive: false },
  { key: 'previous_pet_details',      category: 'Tapasztalat', label: 'Korábbi állatok részletei',sensitive: false },
  { key: 'has_veterinarian',          category: 'Tapasztalat', label: 'Van-e állatorvosa?',       sensitive: false },
  { key: 'financial_background',      category: 'Háttér',      label: 'Anyagi háttér',            sensitive: true  },
  { key: 'family_plans',              category: 'Háttér',      label: 'Családi tervezés (baba, költözés)', sensitive: true },
  { key: 'planned_animal_function',   category: 'Állat célja', label: 'Az állat tervezett funkciója', sensitive: false, options: ['kedvenc','házőrző','munkakutya','terápiás','egyéb'] },
  { key: 'id_card_number',            category: 'Hivatalos',   label: 'Személyi igazolvány szám', sensitive: true  },
] as const;

export type AdopterProfileFieldKey = typeof ADOPTER_PROFILE_FIELDS[number]['key'];

/**
 * Az örökbefogadási igényhez alapértelmezésben kötelező kérdőív.
 *
 * Ezt akkor használjuk, ha az adott állathoz (`animals.required_adopter_fields`)
 * és a menhelyhez (`shelter_adoption_policies.default_required_fields`) sincs
 * beállítva mezőlista. Enélkül a kérdőív blokkja egyszerűen NEM jelent meg, és
 * az igény leadásához elég volt egy telefonszám — miközben a menhely épp az
 * életkörülményekről akar dönteni. Márpedig egy frissen felvett menhelynek
 * alapból nincs policy sora, tehát ez volt az alapeset, nem a kivétel.
 *
 * A `sensitive: true` mezők (gyermekek életkora, anyagi háttér, családi
 * tervezés, személyi igazolvány szám) SZÁNDÉKOSAN maradnak ki: ezeket csak
 * az a menhely kérheti, amelyik kifejezetten beállítja őket. Ez az
 * adattakarékosság (GDPR Art. 5(1)(c)) elvárása — l. compliance.md 4.4, ami
 * külön nevesíti az `id_card_number` bekérését.
 */
export const DEFAULT_REQUIRED_ADOPTER_FIELDS: AdopterProfileFieldKey[] =
  ADOPTER_PROFILE_FIELDS.filter(f => !f.sensitive).map(f => f.key);

export interface AdopterProfile {
  id: string;
  housingType: string | null;
  housingDetails: string | null;
  householdMembers: number | null;
  hasChildren: boolean | null;
  childrenAges: string | null;
  hasOtherPets: boolean | null;
  otherPetsDetails: string | null;
  dailyRoutine: string | null;
  workSchedule: string | null;
  previousPetExperience: boolean | null;
  previousPetDetails: string | null;
  hasVeterinarian: boolean | null;
  financialBackground: string | null;
  familyPlans: string | null;
  plannedAnimalFunction: string | null;
  idCardNumber: string | null;

  // ─── Smart Match életmód-válaszok (l. LIFESTYLE_QUESTIONS) ─────────────
  // Ugyanaz az adat, amit a matcher kérdez — így a két felület egyetlen
  // forrásból dolgozik, és a matchinget nem kell újra végigkattintani.
  lifestyleHousing: string | null;
  lifestyleActivity: string | null;
  lifestyleExperience: string | null;
  lifestyleOtherPets: string | null;
  lifestyleKids: string | null;

  // ─── Lakóhely a távolság-alapú pontozáshoz ────────────────────────────
  // Szándékosan TELEPÜLÉS szintű, nem pontos lakcím (adattakarékosság).
  homeCity: string | null;
  homeLat: number | null;
  homeLng: number | null;
  homePlaceId: string | null;
  /** Vállalt távolság km-ben; `null` esetén DEFAULT_MAX_TRAVEL_KM. */
  maxTravelDistanceKm: number | null;

  createdAt: string;
  updatedAt: string;
}

/** Az `adopter_profiles` életmód-oszlopaiból matcher-profilt épít. */
export const lifestyleFromAdopterProfile = (
  p: Pick<AdopterProfile, 'lifestyleHousing' | 'lifestyleActivity' | 'lifestyleExperience' | 'lifestyleOtherPets' | 'lifestyleKids'> | null | undefined
): LifestyleProfile => ({
  housing: p?.lifestyleHousing ?? '',
  activity: p?.lifestyleActivity ?? '',
  experience: p?.lifestyleExperience ?? '',
  otherPets: p?.lifestyleOtherPets ?? '',
  kids: p?.lifestyleKids ?? '',
});

export interface ShelterAdoptionPolicy {
  id: string;
  shelterId: string;
  defaultRequiredFields: string[];
  requiresMultipleVisits: boolean;
  requiresHomeCheck: boolean;
  requiresSpayNeuter: boolean;
  spayNeuterDeadlineDays: number | null;
  contractType: 'szigorú' | 'egyszerű';
  requiresPhotoReports: boolean;
  photoReportFrequency: string | null;
  allowsThirdPartyTransfer: boolean;
  defaultAdoptionFee: number;
  policyNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Menhelyenkénti értesítési beállítások: mennyi idővel korábban kérjenek
 * emlékeztetőt egy-egy egészségügyi esemény típusra (lead_days_by_type), és
 * mennyi időre halasztódjon egy "Később" gombbal elhalasztott értesítés.
 */
export interface ShelterNotificationSettings {
  id: string;
  shelterId: string;
  snoozeDurationMinutes: number;
  leadDaysByType: Partial<Record<MedicalEventType, number>>;
  createdAt: string;
  updatedAt: string;
}

// ─── Helpers ────────────────────────────────────────────────

export const getDate = (daysDiff: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + daysDiff);
  return d.toISOString().split('T')[0];
};

export const formatHUF = (amount: number): string =>
  new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(amount);

/** Safely get image URL from an entity that may have snake_case or camelCase. */
export const getImageUrl = (entity: any): string | null => {
  return entity?.imageUrl || entity?.image_url || null;
};

/** Safely get a numeric field that may be snake_case or camelCase. */
export const getAmount = (entity: any, camelKey: string, snakeKey: string): number => {
  const val = entity?.[camelKey] ?? entity?.[snakeKey];
  return typeof val === 'number' ? val : 0;
};

const ADDRESS_COUNTRY_NAMES = ['magyarország', 'hungary'];

/**
 * Reduces a full street address (staff-entered or Google Places
 * formatted_address) down to just the city/town — used on public-facing
 * pages where the exact street/house number of a stray-found location
 * shouldn't be exposed. Heuristic: split on commas, drop the country
 * segment, strip a leading 4-digit Hungarian postal code, and return the
 * first remaining segment that has no digits left (a house number always
 * has one; a city name normally doesn't).
 */
export const extractCityFromAddress = (address: string | null | undefined): string => {
  if (!address) return '';
  const segments = address.split(',').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return '';
  const withoutCountry = segments.filter(s => !ADDRESS_COUNTRY_NAMES.includes(s.toLowerCase()));
  const candidates = withoutCountry.length > 0 ? withoutCountry : segments;

  const stripPostalCode = (s: string) => s.replace(/^\d{4}\s+/, '');

  for (const segment of candidates) {
    const stripped = stripPostalCode(segment);
    if (!/\d/.test(stripped)) return stripped;
  }
  // Nothing looked unambiguously city-like (e.g. a single segment with a
  // house number) — fall back to the first segment rather than nothing.
  return stripPostalCode(candidates[0]);
};
